import { api, onWrite, type MyWorkSummary } from './api'

// The one count of My work, shared by the sidebar badge, the bell and Home's
// "Waiting for you", so the three can never show two different numbers. Each
// opens My work on the whole list (MY_WORK_ALL), so the number on the door is
// the number of rows behind it.
//
// # WHY ONLY THE COUNT
//
// The badge and the bell need a number, not the list. They ask for the counts
// alone (GET /v1/my-work?count=only), and the list is read by the My work
// screen, one filter and one page at a time.
//
// # WHY HOME IS NOT ASKED TWICE
//
// Home's panel carries the same count, assembled in the same request that
// counts its figures. On Home the shell takes the count from the panel
// (countFrom) instead of asking for it again: the shell's first read waits one
// turn, so Home, which mounts in the same commit, says first that its panel is
// on the way. A My work page the screen reads carries the counts as well and
// replaces the cached ones.
//
// # WHY IT IS CACHED FOR A MOMENT
//
// The count reads again when the address changes, so a number goes down on the
// way back from doing a task. Walking through five screens should not ask five
// times; fifteen seconds is short enough that a count is right by the time
// somebody looks back at it, and any change the API accepts reads it again at
// once (onWrite).

export const MY_WORK_ALL = '#/my-work?show=all'

const FRESH_MS = 15_000
let cached: { at: number; value: Promise<MyWorkSummary> } | null = null
const listeners = new Set<() => void>()

function keep(value: Promise<MyWorkSummary>) {
  cached = { at: Date.now(), value }
  // A failed read is not kept, so the next screen asks again.
  value.catch(() => {
    if (cached?.value === value) cached = null
  })
}

export function loadMyWorkCount(force = false): Promise<MyWorkSummary> {
  if (!force && cached && Date.now() - cached.at < FRESH_MS) return cached.value
  const value = api.myWorkCount()
  keep(value)
  return value
}

function tell() {
  for (const l of listeners) l()
}

// Take the count from a read that carries it anyway: Home's panel while it is
// on the way, or a page of My work. A panel without one (an older API), or one
// that failed, falls back to asking.
export function countFrom(pending: Promise<MyWorkSummary | undefined | null>) {
  const value = pending.then(
    (s) => (s && typeof s.total === 'number' ? { total: s.total, counts: s.counts } : api.myWorkCount()),
    () => api.myWorkCount(),
  )
  keep(value)
  value.then(tell, () => undefined)
}

// Forget the last count after a task was done, and tell every count to read
// again. Several changes in a row (a bulk action) read once.
let soon: ReturnType<typeof setTimeout> | null = null
export function myWorkChanged() {
  cached = null
  if (soon) clearTimeout(soon)
  soon = setTimeout(() => {
    soon = null
    tell()
  }, 200)
}

export function onMyWorkChanged(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

// Any change the API accepted may have taken a row off the list.
onWrite(myWorkChanged)
